"use client";

import { useEffect, useState } from "react";

import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { useUser } from "@/contexts/user-provider";

import { useEventTitle } from "@/hooks/use-event-title";

import { getFeeStatement } from "@/lib/appointment-fee-statement";
import { formatCurrency } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { computeNoShowFeeCents } from "@/lib/service-fees";

import type { AppointmentFees, EventTiny } from "@/types/event";

import { useAppointmentNotificationStore } from "@/stores/appointment-notification-store";

type NoShowService = {
  price?: number;
  bookingFeePercentage?: number;
  cancellationFeePctWithin24h?: number;
  cancellationFeePctWithin48h?: number;
  noShowFee?: number | null;
  noShowFeePercentage?: number;
};

type NoShowSegment = {
  service?: NoShowService | null;
  hasNoShowSmsSequenceConfigured?: boolean;
};

type TitleOnlyNoShowEvent = {
  title?: string | null;
  segments?: NoShowSegment[];
  fees?: AppointmentFees | null;
};

export type AppointmentNoShowEvent = EventTiny | TitleOnlyNoShowEvent;

const isFullEventTiny = (event: AppointmentNoShowEvent): event is EventTiny =>
  "type" in event &&
  "start" in event &&
  "attendees" in event &&
  "segments" in event;

const hasServiceNoShowFeeConfigured = (
  service?: NoShowService | null,
): boolean => !!service && computeNoShowFeeCents(service) > 0;

const getSegmentService = (
  segment: EventTiny["segments"][number] | NoShowSegment,
): NoShowService | null | undefined =>
  "service" in segment ? segment.service : undefined;

const hasEventNoShowFeeConfigured = (event?: AppointmentNoShowEvent): boolean =>
  event?.segments?.some((segment) =>
    hasServiceNoShowFeeConfigured(getSegmentService(segment)),
  ) ?? false;

const hasEventNoShowSmsConfigured = (
  event?: AppointmentNoShowEvent,
): boolean => {
  const serviceSegments =
    event?.segments?.filter((segment) => "service" in segment) ?? [];
  const primaryService = serviceSegments[0];

  return !!(
    primaryService &&
    "hasNoShowSmsSequenceConfigured" in primaryService &&
    primaryService.hasNoShowSmsSequenceConfigured
  );
};

interface Props {
  isOpen?: boolean;
  setIsOpen?: (val: boolean) => void;
  onConfirm: (
    chargeNoShowFee?: boolean,
    sendSms?: boolean,
  ) => void | Promise<void>;
  event?: AppointmentNoShowEvent;
  disabled?: boolean;
  isLoading?: boolean;
  showNoShowFeeToggle?: boolean;
  autoChargeCardOnFile?: boolean;
}

export default function AppointmentNoShowConfirmationModal({
  isOpen,
  setIsOpen,
  onConfirm,
  event,
  disabled = false,
  isLoading = false,
  showNoShowFeeToggle = false,
  autoChargeCardOnFile = false,
}: Props) {
  const getEventTitle = useEventTitle();
  const appointmentTitle = event
    ? isFullEventTiny(event)
      ? getEventTitle(event) || "this appointment"
      : event.title?.trim() || "this appointment"
    : "this appointment";
  const { sendNoShowMessage, setSendNoShowMessage } =
    useAppointmentNotificationStore();
  const { currentUser } = useUser();
  const canWaiveFee = hasPermission(
    currentUser,
    PERMISSIONS.SCHEDULING_NO_SHOW_FEE_SKIP,
  );
  // The fee is charged unless someone allowed to waive it unchecks the box, and
  // a waiver is never remembered for the next no-show.
  const [waiveRequested, setWaiveRequested] = useState(false);
  const shouldChargeNoShowFee = !(canWaiveFee && waiveRequested);
  const [shouldSendNoShowMessage, setShouldSendNoShowMessage] =
    useState(sendNoShowMessage);
  const shouldShowNoShowFeeToggle =
    showNoShowFeeToggle && hasEventNoShowFeeConfigured(event);

  // Quoted by the server with the same logic that creates the charge.
  const noShowFeeCents = event?.fees?.noShowFee ?? 0;
  const feeAmount = noShowFeeCents > 0 ? formatCurrency(noShowFeeCents) : null;
  const shouldShowNoShowMessageToggle = hasEventNoShowSmsConfigured(event);
  const hasCustomContent =
    shouldShowNoShowFeeToggle || shouldShowNoShowMessageToggle;

  useEffect(() => {
    if (isOpen) {
      setWaiveRequested(false);
      setShouldSendNoShowMessage(sendNoShowMessage);
    }
  }, [isOpen, sendNoShowMessage]);

  const handleConfirm = async () => {
    await onConfirm(
      shouldShowNoShowFeeToggle ? shouldChargeNoShowFee : undefined,
      shouldShowNoShowMessageToggle ? shouldSendNoShowMessage : undefined,
    );
    if (shouldShowNoShowMessageToggle) {
      setSendNoShowMessage(shouldSendNoShowMessage);
    }
  };

  return (
    <DeleteConfirmationModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleConfirm}
      title="Mark as No-Show"
      subtitle={
        shouldShowNoShowMessageToggle
          ? `Are you sure you want to mark ${appointmentTitle} as a no-show? You can choose whether to text the patient.`
          : `Are you sure you want to mark ${appointmentTitle} as a no-show?`
      }
      customContent={
        hasCustomContent ? (
          <div className="space-y-3">
            {/* Roles that can't waive the fee get no checkbox, just what will happen. */}
            {shouldShowNoShowFeeToggle && !canWaiveFee && (
              <p
                data-testid="no-show-fee-statement"
                className="text-body text-foreground"
              >
                {getFeeStatement({
                  feeLabel: "no-show fee",
                  feeAmount,
                  feeIsKnowable: event?.fees != null,
                  autoChargeCardOnFile,
                })}
              </p>
            )}

            {shouldShowNoShowFeeToggle && canWaiveFee && (
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="chargeNoShowFee"
                    checked={shouldChargeNoShowFee}
                    onCheckedChange={(checked) => {
                      setWaiveRequested(!checked);
                    }}
                    disabled={isLoading}
                  />
                  <Label
                    htmlFor="chargeNoShowFee"
                    className="cursor-pointer text-body-strong"
                  >
                    Charge no-show fee
                  </Label>
                </div>
                {shouldChargeNoShowFee &&
                  (feeAmount || autoChargeCardOnFile) && (
                    <p className="pl-6 text-caption text-muted-foreground">
                      {feeAmount
                        ? autoChargeCardOnFile
                          ? `${feeAmount} will be charged to the card on file.`
                          : `${feeAmount} no-show fee will be added.`
                        : "Card on file will be charged if applicable."}
                    </p>
                  )}
              </div>
            )}

            {shouldShowNoShowMessageToggle && (
              <div
                className={
                  shouldShowNoShowFeeToggle
                    ? "flex flex-col justify-between border-t pt-3 md:flex-row md:items-center"
                    : "flex flex-col justify-between md:flex-row md:items-center"
                }
              >
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="sendNoShowMessage"
                    checked={shouldSendNoShowMessage}
                    onCheckedChange={(checked) => {
                      setShouldSendNoShowMessage(!!checked);
                    }}
                    disabled={isLoading}
                  />
                  <Label
                    htmlFor="sendNoShowMessage"
                    className="cursor-pointer text-body font-medium"
                  >
                    Send no-show message to patient
                  </Label>
                </div>
                <span className="text-caption text-muted-foreground">
                  {shouldSendNoShowMessage
                    ? "Message will be sent"
                    : "No message will be sent"}
                </span>
              </div>
            )}
          </div>
        ) : undefined
      }
      confirmText="Mark No-Show"
      disabled={disabled}
      isLoading={isLoading}
      variant="destructive"
    />
  );
}
